import { GET_PERSON_LOCATIONS } from "./components/StaffLocationTable"
import { GET_COMPANY_LOCATIONS } from "../../features/graphql/query"
import { gql, useQuery, useMutation } from "@apollo/client"
import { GridColDef, GridRowModel, GridRowSelectionModel, GridRowId, GridRow, GridCallbackDetails } from "@mui/x-data-grid"
import { useEffect, useState } from "react"
import usePersonLocationAssignment from "./hooks/usePersonLocationAssignment"
import { useParams } from "react-router"
import { Box, Button, Card } from "@mui/material"
import { TablePaginated, TableStandard, TableToolBar, useManageModal } from "ui"
import { LoadingButton } from "@mui/lab"
const GET_PERSON_WITH_LOCATIONS_FOR_LINK = gql(`
      query getPersonWithLocationsForLink($personId: UUID!){
        person(id:$personId){
          id
          firstName
          lastName
          singleStudents{
                id
                locationId
        }
          locations {
            edges{
              node{
                id
              }
            }
          }
          company {
            locations{
              id
              name
            }
          }
        }}`)
function deltaSelections(a: GridRowId[], b: GridRowId[], c: GridRowId[]): GridRowId[] {

    const added = a.filter(x => !b.includes(x));
    const removed = b.filter(x => !a.includes(x));

    const updated = [
        ...c.filter((x: any) => !removed.includes(x)),
        ...added
    ]
    return ([] as GridRowId[])
        .concat(c.filter(x => !removed.includes(x)))
        .concat(added);
}

const unique = <T,>(array: T[]): T[] => {
    return Array.from(new Set(array));
}
const TABLE_COLUMNS: GridColDef[] = [{
    field: "name", headerName: "Location Name",
    flex: 1,
    sortable: true,
}]
function mapDataToTable(data: any) {
    const _data: any = []
    data.company.locations.forEach((location: any) => {
        const studentsAtLocation = data.singleStudents?.filter((student: any) => student.locationId === location.id)?.map((edge: any) => edge.node) || []
        _data.push({
            id: location.id,
            name: location.name,
            singleStudents: studentsAtLocation,
        })
    })

    return _data
}
const AssignPersonLocationPage = ({ onBack }: { onBack: () => void }) => {
    const { pid } = useParams()
    const { openModal } = useManageModal()
    const [tableState, setTableState] = useState<{
        columns: GridColDef[],
        rows: GridRowModel[],
        initialSelectionModel: GridRowSelectionModel,
        selectionModel: GridRowSelectionModel,
        selections: {
            initial: GridRowId[],
            pageAssign: GridRowId[],
            prevAssign: GridRowId[],
            nextAssign: GridRowId[],
            unassign: GridRowId[],
            allSelected: GridRowId[]
        },
    }>({
        columns: [],
        rows: [],
        initialSelectionModel: { type: 'include', ids: new Set() },
        selectionModel: { type: 'include', ids: new Set() },
        selections: {
            initial: [],
            pageAssign: [], // Contains student IDs that have a relationship with person
            prevAssign: [], // Contains student IDs across page/table
            nextAssign: [], // Contains student IDs used to created a person/student relationship
            unassign: [],   // Contains student IDs used to remove the person/student relationship
            allSelected: [] // Contains student IDs for selections on table/page
        },
    });
    const { savePersons } = usePersonLocationAssignment(pid ?? '')
    const { loading, data, fetchMore } = useQuery(GET_PERSON_WITH_LOCATIONS_FOR_LINK,
        {
            notifyOnNetworkStatusChange: true,
            fetchPolicy: "cache-and-network",
            variables: {
                personId: pid
            }
        }
    )
    const [saving, setSaving] = useState<boolean>(false);
    useEffect(() => {
        if (loading || !data) return;
        setTableState((prevState) => {
            const columns = [...TABLE_COLUMNS]
            const rows = mapDataToTable(data.person)
            const selectionModel: GridRowSelectionModel = { type: 'include', ids: new Set<GridRowId>() }
            for (let i = 0; i < data?.person?.locations?.edges.length; i++) {
                const personLocations = data?.person?.locations?.edges ?? [];
                if (!personLocations) continue;
                const workingLocation = personLocations[i].node.id;
                for (let k = 0; k < data?.person?.company?.locations.length; k++) {
                    const companyLocation = data?.person?.company?.locations[k];
                    const isAssigned = workingLocation == companyLocation.id;
                    if (isAssigned) {
                        selectionModel.ids.add(workingLocation)
                    }
                }
            }

            const _ids = Array.from(selectionModel.ids);

            return {
                ...prevState,
                columns: columns,
                rows: rows,
                selectionModel,
                selections: {
                    ...prevState.selections,
                    initial: _ids,
                    pageAssign: _ids,
                    prevAssign: unique(prevState.selections.prevAssign.concat(Array.from(selectionModel.ids))),
                    allSelected: Array.from(selectionModel.ids).concat(prevState.selections.nextAssign)

                }
            }
        })
    }, [loading, data])
    const handleSave = async () => {
        if (tableState.selections.nextAssign.length === 0 && tableState.selections.unassign.length === 0) return;
        setSaving(true)

        await savePersons(tableState.selections.nextAssign, tableState.selections.unassign)
        setTableState(prevState => ({
            ...prevState,
            selections: {
                ...prevState.selections,
                pageAssign: [],
                prevAssign: [],
                nextAssign: [],
                unassign: []
            }
        }));
        openModal({ type: "SAVED_ASSIGNMENTS" })
        setSaving(false)
    }

    const handleSelectionModelChange = (values: GridRowSelectionModel, row: GridCallbackDetails) => {
        const selectedIds = Array.from(values.ids);

        setTableState(prev => {
            const currentPageIds = new Set(prev.rows.map(r => r.id));
            const allSelected = new Set(prev.selections.allSelected);
            const prevPageSelected = Array.from(prev.selectionModel.ids);
            const assign = selectedIds.filter(id => !prevPageSelected.includes(id));
            const unassign = prevPageSelected.filter(id => !selectedIds.includes(id));


            // User selected rows -> add to global selection
            selectedIds.forEach(id => allSelected.add(id));

            // User deselected rows -> remove from global selection
            currentPageIds.forEach(id => {
                if (!selectedIds.includes(id)) allSelected.delete(id);
            });

            // Page-level selection for DataGrid display
            const pageSelectionIds = Array.from(allSelected).filter(id => currentPageIds.has(id));

            return {
                ...prev,
                selectionModel: { type: 'include', ids: new Set(pageSelectionIds) },
                selections: {
                    ...prev.selections, allSelected: Array.from(allSelected),
                    nextAssign: unique(deltaSelections(assign, unassign, prev.selections.nextAssign)),
                    unassign: deltaSelections(unassign, assign, prev.selections.unassign),
                },


            };
        });
    };


    return (
        <Card elevation={4} style={{
            width: "100%",
            height: "auto",
        }}>
            <TableStandard loading={loading}
                renderToolbar={({ searchQuery, onSearch, onSearchClear }) => {
                    return (
                        <TableToolBar title={`Assign ${data?.person.firstName} ${data?.person.lastName} to new Locations`}
                            searchQuery={searchQuery}
                            onSearch={onSearch}
                            onBack={onBack}
                            onSearchClear={onSearchClear} />
                    )
                }}
                columns={tableState.columns}
                rows={tableState.rows}
                slots={{
                    row: (props: any) => {
                        const location = props.row
                        const selected = tableState.selections.initial.includes(location.id);
                        const isOnlyContactAtLocation = location.singleStudents.length > 0
                        const disabled = (isOnlyContactAtLocation && selected);
                        const handleDisabledClick = () => {
                            openModal({ type: "ONLY_STUDENT_REF" });
                        }

                        return (
                            <div
                                style={{
                                    background: disabled ? '#F5F5F5' : 'initial',
                                    color: disabled ? '#777' : 'initial',
                                    cursor: disabled ? 'not-allowed' : 'default'
                                }}
                                onClick={disabled ? handleDisabledClick : undefined}>
                                <GridRow {...props} />
                            </div>
                        )
                    }
                }}
                isRowSelectable={(params) => {
                    const selected = tableState.selections.initial.includes(params.row.id);
                    const hasNoSingleStudents = params.row.singleStudents.length === 0;

                    return hasNoSingleStudents;
                }}
                selectionProps={{
                    rowSelectionModel: { type: 'include', ids: new Set(tableState.selections.allSelected) },
                    onRowSelectionModelChange: handleSelectionModelChange
                }}
                disableBoxShadow={true}
                checkboxSelection={true}
            />
            <AssignPersonLocationPage.Footer
                disabled={(tableState.selections.nextAssign.length === 0 && tableState.selections.unassign.length === 0)}
                saving={saving}
                onBack={onBack}
                onSave={handleSave}
            />
        </Card>
    )
}

AssignPersonLocationPage.Footer = ({ disabled, saving, onSave, onBack }: { saving: boolean, disabled: boolean, onBack: () => void, onSave: () => void }) => {
    return (
        <Box component="div"
            sx={{ padding: "16px", display: "flex", justifyContent: "flex-end" }}
        >
            <Button
                disableElevation
                variant="contained"
                size="large"
                sx={{
                    marginRight: "16px",
                    backgroundColor: "#C4C4C4",
                    "&:hover": {
                        color: "#FFF",
                        backgroundColor: "red.main",
                    },
                }}
                onClick={onBack}
            >
                Cancel
            </Button>
            <LoadingButton
                loading={saving}
                disabled={disabled}
                disableElevation
                variant="contained"
                size="large"
                sx={{
                    backgroundColor: "primary.main",
                    "&:hover": {
                        backgroundColor: "primary.light",
                    },
                }}
                onClick={onSave}
            >
                Save
            </LoadingButton>
        </Box>
    )
}
export default AssignPersonLocationPage